import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Copy, ShieldCheck, Upload, X } from "lucide-react";
import { makeQrDataUrl } from "../utils/pdfGenerator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PaymentModal = ({ payload, onConfirm, onClose, submitting, error }) => {
  const [qr, setQr] = useState("");
  const [upiId, setUpiId] = useState("");
  const [copied, setCopied] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    axios.get(`${API}/settings/upi`).then((r) => setUpiId(r.data.upi_id)).catch(() => setUpiId("ttdpayment@jio"));
  }, []);

  useEffect(() => {
    if (!upiId) return;
    const note = encodeURIComponent(`${payload.service} booking`);
    makeQrDataUrl(`upi://pay?pa=${upiId}&pn=TT%20Devasthanam&am=${payload.amount}&cu=INR&tn=${note}`)
      .then(setQr)
      .catch(() => {});
  }, [upiId, payload]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(upiId); } catch { /* clipboard may be blocked */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setLocalError("");
  };

  const confirm = () => {
    if (!file) return setLocalError("Please upload your payment screenshot to confirm the booking.");
    onConfirm(file);
  };

  return (
    <div className="modal-backdrop">
      <motion.div
        className="confirmation pay-modal"
        role="dialog"
        aria-modal="true"
        data-testid="payment-modal"
        initial={{ opacity: 0, y: 28, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      >
        <button className="modal-close" onClick={onClose} data-testid="close-payment-button"><X size={18} /></button>
        <p className="eyebrow centered">Step 03 — Payment</p>
        <h2>Pay by UPI</h2>
        <p className="confirm-copy">Scan the QR with any UPI app or pay to the UPI ID below, then upload your payment screenshot to receive the e-ticket.</p>
        <div className="pay-amount" data-testid="payment-amount">₹{Number(payload.amount).toLocaleString("en-IN")}<small>{payload.service}{payload.date ? ` · ${payload.date}` : ""}{payload.slot ? ` · ${payload.slot} hrs` : ""}</small></div>
        <div className="pay-grid">
          <div className="pay-qr-wrap">{qr && <img src={qr} alt="UPI payment QR code" className="pay-qr" data-testid="upi-qr-image" />}</div>
          <div className="pay-side">
            <span className="pay-label">Pay to UPI ID</span>
            <div className="upi-id-row">
              <b data-testid="upi-id-text">{upiId || "Loading…"}</b>
              <button type="button" className="copy-btn" onClick={copy} data-testid="upi-copy-button">{copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}</button>
            </div>
            <ol className="pay-steps">
              <li>Open any UPI app — GPay, PhonePe, Paytm, BHIM</li>
              <li>Pay ₹{Number(payload.amount).toLocaleString("en-IN")} by QR or UPI ID</li>
              <li>Upload the payment screenshot below</li>
            </ol>
          </div>
        </div>
        <label className="upload-box" data-testid="payment-upload-box">
          <input type="file" accept="image/png,image/jpeg,image/webp" onChange={pick} data-testid="payment-screenshot-input" />
          {preview
            ? <img src={preview} alt="Payment screenshot preview" className="upload-preview" data-testid="upload-preview" />
            : <><Upload size={20} /><span>{file ? file.name : "Upload payment screenshot (PNG / JPG)"}</span></>}
        </label>
        {(localError || error) && <p className="form-error" data-testid="payment-error">{localError || error}</p>}
        <button className="primary-button form-submit" onClick={confirm} disabled={submitting} data-testid="payment-confirm-button">
          <ShieldCheck size={16} /> {submitting ? "Confirming…" : "I have paid — confirm booking"}
        </button>
      </motion.div>
    </div>
  );
};
